import React from "react";

/* Who may change things on the office console.
 *
 * The operator's rule: "Admin access must have ability to change all the
 * details on the website, rest everyone just can view the details can't
 * edit", and "The IT team should have all access, they can do whatever they
 * want."
 *
 * So admin and IT write; every other console role (today: dispatcher) reads.
 * "it" is its own role rather than an alias for "admin" so the audit trail
 * says who actually made a change, and so the two can diverge later.
 *
 * This is presentation only. The server is the authority - it rejects the
 * write whatever the browser shows. What this module buys the operator is
 * that a dispatcher never presses a live-looking button and gets a 403 back:
 * the control is either gone or visibly dead before they touch it.
 *
 * The driver app is deliberately NOT gated through here. A driver clocking
 * themselves in, uploading proof of delivery, or marking their own stop
 * delivered is doing their job, not "editing the website", and it is already
 * scoped so they only ever touch their own rows.
 */

export const EDIT_ROLES = ["admin", "it"];

// Read the role exactly the way App.js does. Storage can throw outright in a
// locked-down browser profile, and a thrown read here would white-screen the
// whole console - so failure means "no role", which means read-only.
export function currentRole() {
  try {
    return localStorage.getItem("dav_role") || "";
  } catch {
    return "";
  }
}

export function canEdit() {
  return EDIT_ROLES.includes(currentRole());
}

/* Deliberately not stateful: App.js rewrites dav_role when /auth/verify
 * answers, which can land after a screen has already mounted. A value cached
 * in useState would leave an admin stuck on a read-only console until they
 * navigated away; reading on every render always agrees with what App.js
 * last stored. */
export function useCanEdit() {
  return canEdit();
}

// One wording for every disabled control, so the answer to "why can't I type
// in this?" is the same sentence wherever the operator meets it.
export const READ_ONLY_HINT = "View only — ask an admin or IT to change this";

/* One per screen, at the top. Not per section: repeating it down a page
 * stops being information and starts being wallpaper. */
export function ReadOnlyBanner({ className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-2 bg-slate-900/60 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-300 print:hidden ${className}`}>
      <span className="text-[10px] font-bold uppercase tracking-widest text-sky-300 bg-sky-500/10 border border-sky-500/30 rounded px-2 py-0.5 whitespace-nowrap">
        View only
      </span>
      <span>You have view-only access. Ask an admin or IT to make changes.</span>
    </div>
  );
}
